import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, message } from 'antd';
import { PATH } from 'constants/path';
import { ROSTER_STATUS_META } from '../../constants';
import PracticePayersCard from '../PracticePayersCard';
import { isValidEmail } from 'utils/form';
import useCurrentUser from 'hooks/useCurrentUser';
import { TCredentialingProvider, OrgCredentialingPayerRef } from 'types/credentialing';
import {
    addGroupCredentialingProvider,
    deleteGroupCredentialingProvider,
    addGroupCredentialingProvidersByIds,
} from 'api/credentialing';
import s from './s.module.less';

export type OwnerChoice = 'yes' | 'no' | undefined;

interface AddProvidersStepProps {
    title: string;
    subtitle: string;
    roster: TCredentialingProvider[];
    // Refetch the roster from the backend after an add/delete mutation.
    onRefetch?: () => void;
    // Owner-as-provider question (main flow Step 2 only).
    showOwnerQuestion?: boolean;
    // Practice payers block (add-providers page only — existing practice already picked them).
    showPayersCard?: boolean;
    initialPayers?: OrgCredentialingPayerRef[];
}

const OWNER_OPTIONS: { value: 'yes' | 'no'; label: string }[] = [
    { value: 'yes', label: 'Yes, I also see patients' },
    { value: 'no', label: 'No, I only manage the practice' },
];

// Roster + "Add a provider" form. Shared by the main flow's Step 2 (with the owner question) and
// the standalone add-providers page (with the practice payers block instead).
const AddProvidersStep: React.FC<AddProvidersStepProps> = ({
    title,
    subtitle,
    roster,
    onRefetch,
    showOwnerQuestion,
    showPayersCard,
    initialPayers = [],
}) => {
    const navigate = useNavigate();
    const [currentUser] = useCurrentUser();
    const ownerProviderId = currentUser?.provider?.id;

    const [first, setFirst] = useState('');
    const [last, setLast] = useState('');
    const [email, setEmail] = useState('');
    const [addError, setAddError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // Local "No" highlight only — "Yes" is derived from roster membership below.
    const [ownerChoice, setOwnerChoice] = useState<OwnerChoice>(undefined);

    // The owner is a provider iff their own providerId is on the roster.
    const ownerIsProvider = !!ownerProviderId && roster.some((p) => p.providerId === ownerProviderId);
    const ownerSelection: OwnerChoice = ownerIsProvider ? 'yes' : ownerChoice;

    // Map backend onboarding state to UI display status.
    // IN_PROGRESS + PAYERS_SELECTED counts as "complete" (payers picked → the group step is done).
    const getDisplayStatus = (p: TCredentialingProvider): string => {
        if (p.onboardingStatus === 'IN_PROGRESS') {
            return p.onboardingStep === 'PAYERS_SELECTED' ? 'complete' : 'in_progress';
        }
        return 'imported'; // INIT
    };

    // "Yes" adds the owner to the roster as a provider (by their own providerId); "No" removes them.
    const handleOwnerChoice = async (choice: 'yes' | 'no') => {
        if (!ownerProviderId) return;
        if (choice === 'yes') {
            if (ownerIsProvider) {
                setOwnerChoice('yes');
                return;
            }
            const res = await addGroupCredentialingProvidersByIds([ownerProviderId]);
            if (res && !res.error) {
                setOwnerChoice('yes');
                onRefetch?.();
            } else {
                message.error(res?.error || 'Failed to update your provider status');
            }
        } else {
            if (!ownerIsProvider) {
                setOwnerChoice('no');
                return;
            }
            const res = await deleteGroupCredentialingProvider(ownerProviderId);
            if (res && !res.error) {
                setOwnerChoice('no');
                onRefetch?.();
            } else {
                message.error(res?.error || 'Failed to update your provider status');
            }
        }
    };

    const handleAdd = async () => {
        if (!first.trim() || !email.trim()) {
            setAddError('Enter at least a first name and an email.');
            return;
        }
        if (!isValidEmail(email.trim())) {
            setAddError('Please enter a valid email address.');
            return;
        }
        setAddError('');
        setSubmitting(true);
        try {
            const res = await addGroupCredentialingProvider({
                firstName: first.trim(),
                lastName: last.trim(),
                email: email.trim(),
            });
            if (res && !res.error) {
                setFirst('');
                setLast('');
                setEmail('');
                onRefetch?.();
            } else {
                setAddError(res?.error || 'Failed to add provider');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async (p: TCredentialingProvider) => {
        const res = await deleteGroupCredentialingProvider(p.providerId);
        if (res && !res.error) {
            onRefetch?.();
        } else {
            message.error(res?.error || 'Failed to remove provider');
        }
    };

    // Open a provider's full credentialing wizard, acting on their behalf via ?providerId=.
    // The wizard threads that id as OVERRIDE-PROVIDER-ID on every step's API call.
    const handleOpenProvider = (p: TCredentialingProvider) => {
        navigate(`${PATH.CREDENTIALING_START}?providerId=${p.providerId}`);
    };

    // Only providers still being onboarded show in the roster (INIT or IN_PROGRESS); fully COMPLETED ones drop off.
    const visibleRoster = roster.filter((p) => p.onboardingStatus === 'INIT' || p.onboardingStatus === 'IN_PROGRESS');

    const total = visibleRoster.length;
    const completeCount = visibleRoster.filter((p) => getDisplayStatus(p) === 'complete').length;

    return (
        <div className={s.wrap}>
            <div className={s.title}>{title}</div>
            <div className={s.subtitle}>{subtitle}</div>

            {showPayersCard && <PracticePayersCard roster={roster} initialPayers={initialPayers} />}

            {showOwnerQuestion && (
                <div className={s.card}>
                    <div className={s.cardTitle}>Do you see patients yourself?</div>
                    <div className={s.cardDesc}>
                        If you also practice, you’ll be credentialed as a provider too — you’ll appear first in the list
                        below as “You,” with your own 7 steps to complete.
                    </div>
                    <div className={s.ownerOptions}>
                        {OWNER_OPTIONS.map((o) => (
                            <div
                                key={o.value}
                                className={`${s.ownerBtn} ${ownerSelection === o.value ? s.ownerBtnActive : ''}`}
                                onClick={() => handleOwnerChoice(o.value)}
                            >
                                {o.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {total > 0 && (
                <>
                    <div className={s.rosterSummary}>
                        {completeCount} of {total} complete. Click a provider to finish their credentialing details.
                    </div>
                    <div className={s.rosterList}>
                        {visibleRoster.map((p) => {
                            const displayStatus = getDisplayStatus(p);
                            const meta = ROSTER_STATUS_META[displayStatus];
                            const isSelf = showOwnerQuestion && !!ownerProviderId && p.providerId === ownerProviderId;
                            return (
                                <div key={p.providerId} className={s.rosterRow}>
                                    <div className={s.rosterName}>
                                        {p.firstName} {p.lastName}
                                        {isSelf && <span className={s.youBadge}>You</span>}
                                        {p.email && <div className={s.rosterEmail}>{p.email}</div>}
                                    </div>
                                    <span className={s.pill} style={{ background: meta.bg, color: meta.color }}>
                                        {meta.label}
                                    </span>
                                    <Button type="link" className={s.rosterBtn} onClick={() => handleOpenProvider(p)}>
                                        {displayStatus !== 'imported' ? 'Review' : 'Continue'}
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Button>
                                    {!isSelf && (
                                        <Button
                                            type="text"
                                            className={s.deleteBtn}
                                            onClick={() => handleRemove(p)}
                                            aria-label={`Remove ${p.firstName} ${p.lastName}`}
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                <line x1="14" y1="11" x2="14" y2="17" />
                                            </svg>
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <div className={s.card}>
                <div className={s.cardTitle}>Add a provider</div>
                <div className={s.addRow}>
                    <div className={s.addField}>
                        <label>First name</label>
                        <Input value={first} onChange={(e) => setFirst(e.target.value)} />
                    </div>
                    <div className={s.addField}>
                        <label>Last name</label>
                        <Input value={last} onChange={(e) => setLast(e.target.value)} />
                    </div>
                    <div className={`${s.addField} ${s.addFieldWide}`}>
                        <label>Email</label>
                        <Input
                            value={email}
                            placeholder="provider@example.com"
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                </div>
                {addError && <div className={s.addError}>{addError}</div>}
                <Button type="primary" className={s.addBtn} loading={submitting} onClick={handleAdd}>
                    Add
                </Button>
            </div>
        </div>
    );
};

export default AddProvidersStep;
